import { useEffect, useMemo, useState } from 'react'
import {
  Document,
  GroupWithFiles,
  PdfFile,
  PollingResponse,
} from '@components/SignDocumentCommon/types'
import { axiosInstance } from '@/methods/axiosConfig'
import { useInterval } from 'usehooks-ts'
import { devLog } from '@/methods/devLog'
import { actionReport } from '@/methods/actionReport'
import { rootStore } from '@/store/Root.store'
import { API_URL } from '@constants/apiUrl'

function flattenGroups(data: GroupWithFiles[]): Document[] {
  return data.flatMap((group) =>
    group.files.map((file) => ({
      ...file,
      groupId: group.groupId,
      description: group.description,
    }))
  )
}

export const useSignDocumentPolling = (
  groupedDocuments: boolean = false,
  expectSignedClause: boolean = false,
  documentNeedsScrolling: boolean = false,
  showSuccessBlur: boolean = false,
  pollingInterval: number = 1000
) => {
  //  Flags
  const [isLoading, setIsLoading] = useState(true)
  const [btnLoading, setBtnLoading] = useState(false)
  const [isDocReady, setIsDocReady] = useState(false)
  const [shouldPoll, setShouldPoll] = useState(true)
  const [isChecking, setIsChecking] = useState(false)

  // File state
  const [pdfFile, setPdfFile] = useState<PdfFile>()
  const [fileList, setFileList] = useState<Document[]>([])
  const [groups, setGroups] = useState<GroupWithFiles[]>([])
  const [currentDocIndex, setCurrentDocIndex] = useState(0)

  //  showSuccessBlur logic
  const [signSucceeded, setSignSucceeded] = useState(false)

  // documentNeedsScrolling logic
  const [documentScrolled, setDocumentScrolled] = useState(false)

  // Clause state
  const [checkboxKey, setCheckboxKey] = useState(0)
  const [defaultCheckboxChecked, setDefaultCheckboxChecked] = useState(false)
  const [clauseCheckboxChecked, setClauseCheckboxChecked] = useState(false)

  const initHookData = async () => {
    setIsLoading(true)
    try {
      const res = await axiosInstance.get<GroupWithFiles[] | Document[]>(
        `${API_URL}/electronic-id/${groupedDocuments ? 'grouped-list-of-documents' : 'list-of-documents'}`,
        {
          withCredentials: true,
        }
      )
      const listOfDocuments = groupedDocuments
        ? flattenGroups(res.data as GroupWithFiles[])
        : (res.data as Document[])

      setGroups(groupedDocuments ? (res.data as GroupWithFiles[]) : [])
      devLog(listOfDocuments)

      if (listOfDocuments.length === 0) {
        rootStore.ScenarioState.nextPage()
      }

      if (listOfDocuments.length === 1) {
        const { data } = await axiosInstance.get<{ signedUrl: string }>(
          `/electronic-id/pdf-document-presigned`,
          {
            withCredentials: true,
          }
        )

        setPdfFile({
          url: data.signedUrl,
          id: listOfDocuments[0].fileId,
          name: listOfDocuments[0].name || 'pdf document',
          groupId: listOfDocuments[0].groupId,
          description: listOfDocuments[0].description,
        })

        actionReport({
          type: 'event.onboarding-web.agreement.AGREEMENT_DISPLAYED',
          payload: {
            name: listOfDocuments[0].name,
            documentNumber: 1,
            totalDocuments: 1,
          },
        })
      } else if (listOfDocuments.length > 1) {
        setFileList(listOfDocuments)
        await fetchDocumentByIndex(currentDocIndex, listOfDocuments)
      }

      setIsDocReady(true)
    } catch (e) {
      devLog(e)
    }
    setIsLoading(false)
  }

  const fetchDocumentByIndex = async (index: number, documents?: Document[]): Promise<void> => {
    const docsToUse = documents || fileList
    if (!docsToUse || index < 0 || index >= docsToUse.length) {
      devLog('Invalid document index for fetching fileList: ', docsToUse, ' index: ', index)
      return
    }

    const doc = docsToUse[index]

    try {
      const { data } = await axiosInstance.get<{ signedUrl: string }>(
        `/electronic-id/pdf-document-presigned`,
        {
          withCredentials: true,
          params: {
            fileId: doc.fileId,
          },
        }
      )

      const docName = doc.name || `Document ${index + 1}`

      setPdfFile({
        id: doc.fileId,
        name: docName,
        url: data.signedUrl,
        groupId: doc.groupId,
        description: doc.description,
      })

      actionReport({
        type: 'event.onboarding-web.agreement.AGREEMENT_DISPLAYED',
        payload: {
          name: docName,
          documentNumber: index + 1,
          totalDocuments: docsToUse.length,
        },
      })
    } catch (error) {
      devLog('Error fetching document URL', error)
    }
  }

  const fetchNextDocument = async () => {
    if (!fileList || !fileList[currentDocIndex]) return
    setIsDocReady(false)

    try {
      if (currentDocIndex < fileList.length - 1) {
        setCurrentDocIndex(currentDocIndex + 1)
        setDefaultCheckboxChecked(false)

        setCheckboxKey((prev) => prev + 1)

        expectSignedClause && setClauseCheckboxChecked(false)
        documentNeedsScrolling && setDocumentScrolled(false)

        await fetchDocumentByIndex(currentDocIndex + 1)

        setIsDocReady(true)
      }
    } catch (error) {
      devLog(error)
    }
  }

  const fetchPreviousDocument = async () => {
    if (!fileList || !fileList[currentDocIndex]) return
    setIsDocReady(false)

    try {
      if (currentDocIndex > 0) {
        const previousDocIndex = currentDocIndex - 1
        setCurrentDocIndex(previousDocIndex)
        setDefaultCheckboxChecked(false)

        setCheckboxKey((prev) => prev + 1)

        expectSignedClause && setClauseCheckboxChecked(false)
        documentNeedsScrolling && setDocumentScrolled(false)

        await fetchDocumentByIndex(previousDocIndex)

        setIsDocReady(true)
      }
    } catch (error) {
      devLog(error)
    }
  }

  const handleSign = async (): Promise<boolean> => {
    const docId = pdfFile?.id || fileList[currentDocIndex]?.fileId
    const docName = pdfFile?.name || fileList[currentDocIndex]?.name

    if (!docId) {
      devLog('No document ID found for signing')
      return false
    }

    try {
      setBtnLoading(true)

      await axiosInstance.post(
        `${API_URL}/electronic-id/accept-document-to-be-signed`,
        {
          fileId: docId,
        },
        {
          withCredentials: true,
        }
      )

      actionReport({
        type: 'event.onboarding-web.agreement.AGREEMENT_CONFIRMED',
        payload: {
          fileId: docId,
          name: docName,
        },
      })

      setBtnLoading(false)

      if (fileList.length && currentDocIndex < fileList.length - 1) {
        fetchNextDocument()
      } else {
        actionReport({
          type: 'event.onboarding-web.agreement.ALL_AGREEMENTS_CONFIRMED',
          payload: {},
        })

        setSignSucceeded(true)
        setTimeout(() => rootStore.ScenarioState.nextPage(), showSuccessBlur ? 2000 : 0)
      }

      return true
    } catch (e) {
      devLog(e)
      setBtnLoading(false)
      return false
    }
  }

  const checkStatus = async (): Promise<PollingResponse | undefined> => {
    setIsChecking(true)

    try {
      const res = await axiosInstance.get<PollingResponse>(
        `${API_URL}/electronic-id/are-documents-to-be-signed-ready-to-display`,
        {
          withCredentials: true,
        }
      )

      if (res.data.status && res.data.status === 'ready') {
        setShouldPoll(false)
      }
      return res.data
    } catch (e) {
      // Ideally this should be a better error handling
      // eslint-disable-next-line no-console
      console.error(e)
    } finally {
      setIsChecking(false)
    }
  }

  useEffect(() => {
    if (!shouldPoll) {
      initHookData()
    }
  }, [shouldPoll])

  useInterval(
    () => {
      if (!isChecking) {
        checkStatus()
      }
    },
    shouldPoll ? pollingInterval : null
  )

  const isButtonDisabled = useMemo(
    () =>
      !isDocReady ||
      !defaultCheckboxChecked ||
      (expectSignedClause && !clauseCheckboxChecked) ||
      (documentNeedsScrolling && !documentScrolled),
    [
      isDocReady,
      defaultCheckboxChecked,
      expectSignedClause,
      clauseCheckboxChecked,
      documentNeedsScrolling,
      documentScrolled,
    ]
  )

  return {
    isLoading,
    btnLoading,
    isDocReady,
    pdfFile,
    fileList,
    groups,
    currentDocIndex,
    isChecking,
    shouldPoll,
    signSucceeded,
    documentScrolled,
    setDocumentScrolled,
    checkboxKey,
    defaultCheckboxChecked,
    setDefaultCheckboxChecked,
    clauseCheckboxChecked,
    setClauseCheckboxChecked,
    handleSign,
    fetchPreviousDocument,
    isButtonDisabled,
  }
}
